'use client'

import { useRef, useEffect, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react'

interface Tilt3DProps {
  children: ReactNode
  /** Max tilt in degrees toward the cursor. */
  max?: number
  /** Resting scale applied on hover. */
  scale?: number
  className?: string
}

/**
 * Premium pointer-driven 3D tilt with a moving light glare and a one-time
 * 3D entrance reveal on scroll. Desktop only — touch pointers and
 * prefers-reduced-motion get a flat, static element (no jank on phones).
 *
 * Two nested layers on purpose: the OUTER plays the entrance keyframe
 * (transform owned by CSS animation) while the INNER owns the live
 * pointer transform — so the two never fight over `transform`.
 */
export function Tilt3D({ children, max = 7, scale = 1.015, className = '' }: Tilt3DProps) {
  const outer = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const glare = useRef<HTMLDivElement>(null)
  const raf = useRef<number | null>(null)

  // One-time 3D entrance when the card scrolls into view.
  useEffect(() => {
    const el = outer.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.style.opacity = '1'
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            el.classList.add('tilt3d-in')
            io.disconnect()
          }
        }
      },
      { threshold: 0.25 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  function onMove(e: ReactPointerEvent) {
    if (e.pointerType === 'touch') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const el = inner.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const px = (e.clientX - r.left) / r.width
    const py = (e.clientY - r.top) / r.height
    const rx = (0.5 - py) * max * 2
    const ry = (px - 0.5) * max * 2
    if (raf.current) cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(() => {
      el.style.transform = `perspective(1200px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(${scale})`
      const g = glare.current
      if (g) {
        g.style.background = `radial-gradient(circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%, rgba(255,255,255,0.22), transparent 55%)`
        g.style.opacity = '1'
      }
    })
  }

  function reset() {
    const el = inner.current
    if (el) el.style.transform = 'perspective(1200px) rotateX(0deg) rotateY(0deg) scale(1)'
    if (glare.current) glare.current.style.opacity = '0'
  }

  return (
    <div ref={outer} className={`tilt3d-outer ${className}`}>
      <div
        ref={inner}
        onPointerMove={onMove}
        onPointerLeave={reset}
        className="tilt3d-inner relative"
        style={{ transformStyle: 'preserve-3d', transition: 'transform .45s cubic-bezier(.22,1,.36,1)', willChange: 'transform' }}
      >
        {children}
        <div
          ref={glare}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{ opacity: 0, transition: 'opacity .35s ease', mixBlendMode: 'overlay' }}
        />
      </div>
    </div>
  )
}
